import { useEffect, useRef, useState } from 'react';

import { Popover, PopoverContent, PopoverTrigger } from '../primitives/popover';
import { ScrollArea, ScrollBar } from '../primitives/scroll-area';
import { BracesIcon } from '../svg/braces-icon';
import { cn } from '../utils/cn';

export type OptionInfoField = {
  key: string;
  missing?: boolean;
  value: unknown;
};

interface OptionInfoPopoverProps {
  className?: string;
  fields?: OptionInfoField[];
}

function stringifyFieldValue(value: unknown) {
  if (typeof value === 'string') {
    return `"${value}"`;
  }

  return JSON.stringify(value, null, 2);
}

/**
 * TODO: Made this functionality more generic and enhance its implementation in controlType with source.
 */
export function OptionInfoPopover({ className, fields = [] }: OptionInfoPopoverProps) {
  const [open, setOpen] = useState(false);
  const [shouldClampHeight, setShouldClampHeight] = useState(false);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const isOverTriggerRef = useRef(false);
  const isOverContentRef = useRef(false);

  const getMaxPopoverHeight = () => Math.min(20 * 16, window.innerHeight * 0.55);

  const updateShouldClampHeight = () => {
    const contentHeight = contentRef.current?.scrollHeight ?? 0;
    setShouldClampHeight(contentHeight > getMaxPopoverHeight());
  };

  const clearCloseTimeout = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const maybeScheduleClose = () => {
    clearCloseTimeout();

    closeTimeoutRef.current = setTimeout(() => {
      if (!isOverTriggerRef.current && !isOverContentRef.current) {
        setOpen(false);
      }
    }, 180);
  };

  const openPopover = () => {
    clearCloseTimeout();
    setOpen(true);
  };

  useEffect(() => {
    return () => {
      clearCloseTimeout();
    };
  }, []);

  useEffect(() => {
    if (!open) return;

    const raf = requestAnimationFrame(updateShouldClampHeight);

    window.addEventListener('resize', updateShouldClampHeight);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', updateShouldClampHeight);
    };
  }, [fields, open]);

  if (fields.length === 0) return null;

  return (
    <Popover modal={false} open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        asChild
        className="data-[state=open]:border data-[state=open]:border-accent data-[state=open]:bg-secondary/50"
      >
        <span
          role="button"
          tabIndex={0}
          aria-label="Ver más información de la opción"
          className={cn(
            'ml-2 inline-flex h-4 w-6 items-center justify-center rounded-full border border-transparent bg-secondary text-secondary-foreground hover:border-accent hover:bg-secondary/50',
            className,
          )}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
          onPointerDown={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
          onPointerEnter={() => {
            isOverTriggerRef.current = true;
            openPopover();
          }}
          onPointerLeave={() => {
            isOverTriggerRef.current = false;
            maybeScheduleClose();
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              setOpen((prev) => !prev);
            }
          }}
        >
          <BracesIcon className="size-3!" />
        </span>
      </PopoverTrigger>
      <PopoverContent
        className="z-10000 w-[24rem] p-4 pr-2 select-text"
        align="start"
        side="bottom"
        onOpenAutoFocus={(event) => event.preventDefault()}
        onCloseAutoFocus={(event) => event.preventDefault()}
        onMouseDown={(event) => {
          event.stopPropagation();
        }}
        onPointerDown={(event) => {
          event.stopPropagation();
        }}
        onPointerUp={(event) => {
          event.stopPropagation();
        }}
        onClick={(event) => {
          event.stopPropagation();
        }}
        onPointerEnter={() => {
          isOverContentRef.current = true;
          openPopover();
        }}
        onPointerLeave={() => {
          isOverContentRef.current = false;
          maybeScheduleClose();
        }}
      >
        <ScrollArea
          className={cn(
            shouldClampHeight ? 'h-[min(20rem,55vh)]' : 'h-auto',
            '[&>[data-radix-scroll-area-viewport]]:overflow-x-auto',
          )}
        >
          <div ref={contentRef} className="w-max min-w-full py-2 pr-4 font-mono text-[11px]">
            <div className="font-semibold text-accent">{'{'}</div>
            {fields.map((field, index) => {
              const hasNext = index < fields.length - 1;

              return (
                <div key={`${field.key}-${index}`} className="pl-4">
                  <span>{`"${field.key}": `}</span>
                  <span
                    className={cn('whitespace-pre', {
                      'font-semibold text-destructive': field.missing,
                    })}
                  >
                    {stringifyFieldValue(field.value)}
                  </span>
                  {hasNext ? <span>,</span> : null}
                </div>
              );
            })}
            <div className="font-semibold text-accent">{'}'}</div>
          </div>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
